import styled from "styled-components";
import { ReactComponent as CrossIcon } from "../../../assets/cross_icon.svg";
import {
  BASIC_BROWN,
  BASIC_DARK_GRAY,
  BASIC_LINE,
  BASIC_ORANGE,
} from "../../../constants/colors";
import {
  OrderCoupon,
  OrderMenu,
  OrderMenuCouponType,
  TaxDetail,
} from "../../../ducks/order";

type Props = {
  title?: string;
  menus: OrderMenu[];
  totalPrice: number;
  totalQuantity: number;
  taxAmount: number;
  coupon?: OrderCoupon;
  taxDetails?: TaxDetail[];
  memo?: string;
  hideMemo?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
  showCartOrderContent?: boolean;
  fontSize?: string;
};

export const OrderContent: React.FC<Props> = ({
  title = "注文内容",
  menus,
  memo,
  totalPrice,
  totalQuantity,
  taxAmount,
  coupon,
  taxDetails,
  hideMemo,
  showCloseButton,
  onClose,
  showCartOrderContent = true,
  fontSize,
}) => {
  return (
    <OrderContentContainer>
      <OrderContentTitle fontSize={fontSize}>{title}</OrderContentTitle>

      {showCloseButton && (
        <CloseButtonContainer $rotate={showCartOrderContent} onClick={onClose}>
          <CrossIcon />
        </CloseButtonContainer>
      )}

      {showCartOrderContent &&
        (totalQuantity === 0 ? (
          <NoItemsText>商品がありません</NoItemsText>
        ) : (
          <>
            <TotalWrapper>
              <OrderDetailContent>
                {menus.map((menu, index) => (
                  <OrderDetailNameArea key={index}>
                    <ItemRow>
                      <ItemName>
                        {menu.name}
                        {menu.quantity > 1 && ` x ${menu.quantity}`}
                      </ItemName>

                      <ItemPrice $strikeThrough={!!menu.coupon}>
                        ¥{(menu.price * menu.quantity).toLocaleString()}
                      </ItemPrice>
                    </ItemRow>
                    {menu.coupon && (
                      <ItemRow $isCoupon>
                        <CouponDetail>
                          {menu.isEarlyBird
                            ? ""
                            : menu.coupon.type === OrderMenuCouponType.Campaign
                              ? "キャンペーン:"
                              : "クーポン:"}
                          {menu.coupon.message}({menu.coupon.discount}円)
                          <CouponPrice>
                            ¥{menu.price * menu.quantity + menu.coupon.discount}
                          </CouponPrice>
                        </CouponDetail>
                      </ItemRow>
                    )}

                    {menu.options.length > 0 && (
                      <Option>{menu.options[0].name}</Option>
                    )}
                  </OrderDetailNameArea>
                ))}
              </OrderDetailContent>
            </TotalWrapper>

            <OrderResult>
              {taxDetails && taxDetails.length > 0 && (
                <TaxDetailWrapper>
                  <TotalWrapper>
                    <Title2>小計</Title2>
                    <TaxPrice>¥{totalPrice.toLocaleString()}</TaxPrice>
                  </TotalWrapper>
                  {taxDetails.map((detail) => (
                    <>
                      <TotalWrapper key={"subtotal_with_tax" + detail.rate}>
                        <Title2>内税対象額 {detail.rate}%</Title2>
                        <TaxPrice>
                          ¥{detail.subtotal_with_tax.toLocaleString()}
                        </TaxPrice>
                      </TotalWrapper>
                      <TotalWrapper key={"tax_" + detail.rate}>
                        <Title2>( 内税 {detail.rate}%</Title2>
                        <TaxPrice>¥{detail.tax.toLocaleString()} )</TaxPrice>
                      </TotalWrapper>
                    </>
                  ))}
                </TaxDetailWrapper>
              )}

              <TotalWrapper>
                <Title>商品数量合計</Title>
                <TotalQuantity>{totalQuantity}点</TotalQuantity>
              </TotalWrapper>

              <TotalWrapper>
                <Title2>商品金額合計</Title2>
                {coupon ? (
                  <TotalPriceContainer>
                    <TotalPrice $strikeThrough>
                      ¥{totalPrice.toLocaleString()}
                    </TotalPrice>

                    <CouponDetail>
                      {coupon.type === OrderMenuCouponType.Campaign
                        ? "キャンペーン"
                        : "クーポン"}
                      :{coupon.message}({coupon.discount}円)
                      <CouponPrice>¥{coupon.price}</CouponPrice>
                    </CouponDetail>
                  </TotalPriceContainer>
                ) : (
                  <TotalPrice>¥{totalPrice.toLocaleString()}</TotalPrice>
                )}
              </TotalWrapper>

              <TotalWrapper>
                <Title2>内消費税等</Title2>
                <TaxPrice>( ¥{taxAmount.toLocaleString()} )</TaxPrice>
              </TotalWrapper>
            </OrderResult>

            {!hideMemo && (
              <MemoContainer>
                <OrderContentTitle>特記事項</OrderContentTitle>
                {memo && <MemoText data-wovn-ignore>{memo}</MemoText>}
              </MemoContainer>
            )}
          </>
        ))}
    </OrderContentContainer>
  );
};

const OrderContentContainer = styled.div`
  position: relative;
  color: ${BASIC_DARK_GRAY};
`;

const OrderContentTitle = styled.div<{ fontSize?: string }>`
  text-align: left;
  margin: 0 0 20px;
  font-weight: 600;
  font-size: ${({ fontSize }) => fontSize || "16px"};
  padding-bottom: 8px;
  border-bottom: 1px solid ${BASIC_LINE};
`;

const CloseButtonContainer = styled.div<{ $rotate: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  color: ${BASIC_DARK_GRAY};
  width: 25px;
  height: 25px;
  cursor: pointer;
  transform: rotate(${({ $rotate }) => ($rotate ? "0" : "45deg")});
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0;
  font-size: 14px;
`;

const OrderDetailContent = styled.div`
  font-weight: 600;
  text-align: left;
  flex: 1;
  position: relative;
`;

const OrderDetailNameArea = styled.div`
  position: relative;
  margin: 0 0 30px 0;
`;

const ItemRow = styled.div<{ $isCoupon?: boolean }>`
  display: flex;
  justify-content: ${({ $isCoupon }) =>
    $isCoupon ? "flex-end" : "space-between"};
  align-items: flex-start;
  margin-bottom: 5px;
  ${({ $isCoupon }) => $isCoupon && "margin-top: -5px;"}
`;

const ItemName = styled.div`
  color: ${BASIC_DARK_GRAY};
  font-size: 14px;
  font-weight: 600;
`;

const ItemPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemPrice = styled.div<{ $strikeThrough?: boolean }>`
  font-size: 14px;
  color: ${BASIC_DARK_GRAY};
  text-align: right;
  font-weight: 300;
  margin-left: 50px;
  ${({ $strikeThrough }) => $strikeThrough && "text-decoration: line-through;"}
`;

const CouponDetail = styled.div`
  display: flex;
  font-size: 12px;
  flex-wrap: wrap;
  align-items: flex-start;
  font-weight: 600;
  color: ${BASIC_BROWN};

  @media (max-width: 780px) {
    flex-direction: column;
  }
`;

const CouponPrice = styled.span`
  font-size: 14px;
  font-weight: 300;
  color: ${BASIC_ORANGE};
  margin-left: 5px;

  @media (max-width: 780px) {
    margin-right: 0;
    margin: 3px 0;
    order: -1;
    align-self: flex-end;
  }
`;

const Option = styled.div`
  color: ${BASIC_DARK_GRAY};
  text-align: left;
  padding: 0;
  margin: 0;
  position: relative;
  font-weight: 300;
  margin-top: 5px;
`;

const OrderResult = styled.div`
  padding: 0 0 18px;
  margin: 0 0 40px;
  border-bottom: 1px solid ${BASIC_LINE};
`;

const Title = styled.div`
  color: ${BASIC_DARK_GRAY};
  text-align: left;
  flex: 1;
  font-weight: 300;
  margin: 0 0 16px;
  padding: 0;
`;

const Title2 = styled.div`
  color: ${BASIC_DARK_GRAY};
  text-align: left;
  flex: 1;
  font-weight: 300;
  margin: 0;
  padding: 0;
`;

const MemoContainer = styled.div``;

const MemoText = styled.div`
  text-align: left;
  font-size: 14px;
  font-weight: 300;
`;

const TotalQuantity = styled.div`
  color: ${BASIC_DARK_GRAY};
  margin: 0;
  padding: 0;
  font-weight: 200;
  font-size: 16px;
`;

const TaxPrice = styled.div`
  font-size: 12px;
  font-weight: 200;
`;

const TotalPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
`;

const TotalPrice = styled.div<{ $strikeThrough?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  padding: 0;
  ${({ $strikeThrough }) => $strikeThrough && "text-decoration: line-through;"}
`;

const TaxDetailWrapper = styled.div`
  margin: 0 0 15px 0;
`;

const NoItemsText = styled.div`
  font-size: 16px;
  color: ${BASIC_DARK_GRAY};
  text-align: center;
  margin: 16px 0;
  padding: 50px 0;
`;
